import { DashboardLayout, adminNavItems } from "@/components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { 
  Map,
  CheckCircle2,
  Clock,
  Circle,
  ChevronDown,
  ChevronRight,
  Users,
  Building2,
  ShoppingBag,
  Wrench,
  FileText,
  Calculator,
  AlertTriangle,
  DollarSign,
  Calendar,
  ArrowRight,
  RotateCcw,
  ClipboardCheck
} from "lucide-react";
import { useState, useEffect } from "react";

// Initial test checklist state
const initialTestChecklist = {
  // Company Client Flow
  cc_create_project: false,
  cc_holding_review: false,
  cc_holding_approve: false,
  cc_client_sign: false,
  cc_tl_apply: false,
  cc_client_select_team: false,
  cc_team_sign: false,
  cc_project_live: false,
  
  // Private Client Flow
  pc_request_quote: false,
  pc_holding_send_quote: false,
  pc_client_accept: false,
  pc_client_sign: false,
  pc_tl_apply: false,
  pc_holding_select_team: false,
  pc_team_sign: false,
  pc_project_live: false,
  
  // Team Leader Flow
  tl_browse_projects: false,
  tl_apply_with_team: false,
  tl_wait_selection: false,
  tl_sign_contract: false,
  tl_daily_progress: false,
  tl_complete_project: false,
  
  // Worker Flow
  w_see_dashboard: false,
  w_view_applications: false,
  w_assigned_to_project: false,
  w_work_on_project: false,
  
  // Holding Admin Flow
  ha_review_projects: false,
  ha_approve_reject: false,
  ha_send_counteroffer: false,
  ha_select_team_private: false,
  ha_manage_workers: false,
  ha_financial_dashboard: false,
  
  // Platform Admin Flow
  pa_manage_webshop: false,
  pa_manage_rentals: false,
  pa_manage_users: false,
  pa_view_analytics: false,
};

export default function AdminRoadmap() {
  const [expandedSections, setExpandedSections] = useState({
    roles: true,
    revenue: true,
    workforce: false,
    stillastjenester: false,
    timeline: false,
    contraoferte: false,
    penalizari: false,
    facturare: false,
    retineri: true,
    testFlows: true
  });
  
  // Load checklist from localStorage
  const [testChecklist, setTestChecklist] = useState(() => {
    const saved = localStorage.getItem('allstillas_test_checklist');
    return saved ? JSON.parse(saved) : initialTestChecklist;
  });
  
  // Save checklist to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('allstillas_test_checklist', JSON.stringify(testChecklist));
  }, [testChecklist]);
  
  const toggleCheck = (key) => {
    setTestChecklist(prev => ({ ...prev, [key]: !prev[key] }));
  };
  
  const resetChecklist = () => {
    setTestChecklist(initialTestChecklist);
  };
  
  const getProgress = (keys) => {
    const checked = keys.filter(k => testChecklist[k]).length;
    return { checked, total: keys.length, percent: Math.round((checked / keys.length) * 100) };
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  return (
    <DashboardLayout navItems={adminNavItems} title="Platform Roadmap">
      <div className="space-y-6" data-testid="admin-roadmap">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-['Unbounded'] font-bold text-[#23F7DD] mb-2">
            🏗️ AllStillas Platform Roadmap
          </h1>
          <p className="text-[#94A3B8]">Overview complet al sistemului planificat</p>
        </div>

        {/* Status Overview */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Card className="bg-green-500/10 border-green-500/30">
            <CardContent className="p-4 flex items-center gap-3">
              <CheckCircle2 className="w-8 h-8 text-green-500" />
              <div>
                <p className="text-green-400 font-bold text-lg">LIVE ACUM</p>
                <p className="text-sm text-[#94A3B8]">Webshop, Rentals</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-green-500/10 border-green-500/30">
            <CardContent className="p-4 flex items-center gap-3">
              <CheckCircle2 className="w-8 h-8 text-green-500" />
              <div>
                <p className="text-green-400 font-bold text-lg">LIVE</p>
                <p className="text-sm text-[#94A3B8]">Proiecte, Holding, Contracte</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-blue-500/10 border-blue-500/30">
            <CardContent className="p-4 flex items-center gap-3">
              <Circle className="w-8 h-8 text-blue-500" />
              <div>
                <p className="text-blue-400 font-bold text-lg">PLANIFICAT</p>
                <p className="text-sm text-[#94A3B8]">Contraoferte, Penalizări</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Section: Structura Roluri */}
        <Card className="bg-[#0A0A0A] border-white/10">
          <CardHeader 
            className="cursor-pointer hover:bg-white/5 transition-colors"
            onClick={() => toggleSection('roles')}
          >
            <CardTitle className="flex items-center justify-between text-white">
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-[#23F7DD]" />
                📊 Structura Roluri
              </div>
              {expandedSections.roles ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
            </CardTitle>
          </CardHeader>
          {expandedSections.roles && (
            <CardContent className="space-y-4">
              {/* Platform Admin */}
              <div className="p-4 bg-[#1e293b] rounded-lg border border-[#334155]">
                <h4 className="text-[#23F7DD] font-bold mb-2">🔷 PLATFORM ADMIN (AllStillas)</h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="p-3 bg-green-500/10 rounded border border-green-500/30">
                    <p className="text-green-400 font-semibold mb-2">✓ Responsabilități:</p>
                    <ul className="text-sm text-[#94A3B8] space-y-1">
                      <li>• Webshop management (vendori, comisioane)</li>
                      <li>• Rentals (100% platformă)</li>
                      <li>• Setări tehnice</li>
                      <li>• Aprobă Holding Company</li>
                    </ul>
                  </div>
                  <div className="p-3 bg-red-500/10 rounded border border-red-500/30">
                    <p className="text-red-400 font-semibold mb-2">✗ NU mai face:</p>
                    <ul className="text-sm text-[#94A3B8] space-y-1">
                      <li>• NU aprobă proiecte</li>
                      <li>• NU semnează contracte</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Holding Admin */}
              <div className="p-4 bg-[#1e293b] rounded-lg border border-[#334155]">
                <h4 className="text-[#23F7DD] font-bold mb-2">🏢 HOLDING ADMIN (Companie Norvegiană)</h4>
                <Badge className="bg-green-500/20 text-green-400 border-green-500/30 mb-3">LIVE</Badge>
                <div className="p-3 bg-green-500/10 rounded border border-green-500/30">
                  <p className="text-green-400 font-semibold mb-2">✓ Responsabilități:</p>
                  <ul className="text-sm text-[#94A3B8] space-y-1 grid md:grid-cols-2 gap-1">
                    <li>• Profil companie (Org Nr, Adresă, Cont bancar)</li>
                    <li>• Verifică & Aprobă proiecte</li>
                    <li>• Negociază cu Clienții</li>
                    <li>• Semnează contractele cu Clienții</li>
                    <li>• Deține TL + Workers (angajați)</li>
                    <li>• Plătește salariile</li>
                    <li>• Reține și plătește skatt, AGA, feriepenger</li>
                    <li>• Dashboard financiar</li>
                  </ul>
                </div>
              </div>

              {/* TL & Worker */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="p-4 bg-[#1e293b] rounded-lg border border-[#334155]">
                  <h4 className="text-white font-bold mb-2">👷 TEAM LEADER</h4>
                  <ul className="text-sm text-[#94A3B8] space-y-1">
                    <li>• Aparține Holding Company</li>
                    <li>• Aplică la proiecte</li>
                    <li>• Formează echipe</li>
                    <li>• Poate face contraoferte</li>
                    <li>• Semnează contract proiect</li>
                  </ul>
                </div>
                <div className="p-4 bg-[#1e293b] rounded-lg border border-[#334155]">
                  <h4 className="text-white font-bold mb-2">👷 WORKER</h4>
                  <ul className="text-sm text-[#94A3B8] space-y-1">
                    <li>• Aparține Holding Company</li>
                    <li>• Asignat de TL la proiecte</li>
                    <li>• Lucrează pe proiecte</li>
                    <li>• Semnează contract proiect</li>
                  </ul>
                </div>
              </div>

              {/* Tipuri Clienti */}
              <div className="grid md:grid-cols-2 gap-4 mt-4">
                <div className="p-4 bg-[#1e293b] rounded-lg border border-[#334155]">
                  <h4 className="text-white font-bold mb-2">🏢 CLIENT BEDRIFT (Companie)</h4>
                  <ul className="text-sm text-[#94A3B8] space-y-1">
                    <li>• Org.nummer <strong className="text-white">OBLIGATORIU</strong></li>
                    <li>• Poate posta joburi WORKFORCE</li>
                    <li>• Poate cere STILLASTJENESTER</li>
                    <li>• Facturare cu scadență (7/14/30 zile)</li>
                    <li>• Semnează contract cu HOLDING</li>
                  </ul>
                </div>
                <div className="p-4 bg-[#1e293b] rounded-lg border border-[#334155]">
                  <h4 className="text-white font-bold mb-2">👤 CLIENT PRIVAT (Persoană)</h4>
                  <ul className="text-sm text-[#94A3B8] space-y-1">
                    <li>• Fără org.nummer</li>
                    <li>• <strong className="text-yellow-400">DOAR</strong> Stillastjenester</li>
                    <li>• Cere ofertă → Negociază → Accept</li>
                    <li>• Semnează contract cu HOLDING</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          )}
        </Card>

        {/* Section: Revenue Streams */}
        <Card className="bg-[#0A0A0A] border-white/10">
          <CardHeader 
            className="cursor-pointer hover:bg-white/5 transition-colors"
            onClick={() => toggleSection('revenue')}
          >
            <CardTitle className="flex items-center justify-between text-white">
              <div className="flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-[#23F7DD]" />
                💰 Revenue Streams Platformă
              </div>
              {expandedSections.revenue ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
            </CardTitle>
          </CardHeader>
          {expandedSections.revenue && (
            <CardContent>
              <div className="grid md:grid-cols-3 gap-4">
                <div className="p-4 bg-[#1e293b] rounded-lg border border-green-500/30">
                  <div className="flex items-center gap-2 mb-3">
                    <ShoppingBag className="w-5 h-5 text-green-400" />
                    <h4 className="text-white font-bold">WEBSHOP</h4>
                    <Badge className="bg-green-500/20 text-green-400 border-green-500/30">LIVE</Badge>
                  </div>
                  <p className="text-[#94A3B8] text-sm mb-2"><strong>Cine plătește:</strong> Vendori</p>
                  <p className="text-[#23F7DD] font-bold">Comision: 2-5%</p>
                </div>
                <div className="p-4 bg-[#1e293b] rounded-lg border border-yellow-500/30">
                  <div className="flex items-center gap-2 mb-3">
                    <FileText className="w-5 h-5 text-yellow-400" />
                    <h4 className="text-white font-bold">PROIECTE</h4>
                    <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">SOON</Badge>
                  </div>
                  <p className="text-[#94A3B8] text-sm mb-2"><strong>Cine plătește:</strong> TL/echipă</p>
                  <p className="text-[#23F7DD] font-bold">Platform fee: 2%</p>
                </div>
                <div className="p-4 bg-[#1e293b] rounded-lg border border-green-500/30">
                  <div className="flex items-center gap-2 mb-3">
                    <Wrench className="w-5 h-5 text-green-400" />
                    <h4 className="text-white font-bold">RENTALS</h4>
                    <Badge className="bg-green-500/20 text-green-400 border-green-500/30">LIVE</Badge>
                  </div>
                  <p className="text-[#94A3B8] text-sm mb-2"><strong>Revenue:</strong> 100%</p>
                  <p className="text-[#23F7DD] font-bold">Echipamente proprii</p>
                </div>
              </div>
            </CardContent>
          )}
        </Card>

        {/* Section: Serviciu 1 - Workforce */}
        <Card className="bg-[#0A0A0A] border-white/10">
          <CardHeader 
            className="cursor-pointer hover:bg-white/5 transition-colors"
            onClick={() => toggleSection('workforce')}
          >
            <CardTitle className="flex items-center justify-between text-white">
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-[#23F7DD]" />
                📋 Serviciu 1: WORKFORCE (B2B)
                <Badge className="bg-green-500/20 text-green-400 border-green-500/30">LIVE</Badge>
              </div>
              {expandedSections.workforce ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
            </CardTitle>
          </CardHeader>
          {expandedSections.workforce && (
            <CardContent>
              <p className="text-[#94A3B8] mb-4 p-3 bg-[#23F7DD]/10 rounded border border-[#23F7DD]/30">
                <strong>Ce este:</strong> Companie cere echipă de muncitori (TL + Workers)
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left p-3 text-[#23F7DD]">Pas</th>
                      <th className="text-left p-3 text-[#23F7DD]">Cine</th>
                      <th className="text-left p-3 text-[#23F7DD]">Acțiune</th>
                    </tr>
                  </thead>
                  <tbody className="text-[#94A3B8]">
                    <tr className="border-b border-white/5"><td className="p-3">1</td><td className="p-3">CLIENT BEDRIFT</td><td className="p-3">Postează job</td></tr>
                    <tr className="border-b border-white/5"><td className="p-3">2</td><td className="p-3">HOLDING</td><td className="p-3">Verifică & Negociază prețul</td></tr>
                    <tr className="border-b border-white/5"><td className="p-3">3</td><td className="p-3">CLIENT</td><td className="p-3">Aprobă termenii</td></tr>
                    <tr className="border-b border-white/5 bg-green-500/5"><td className="p-3">4</td><td className="p-3">CLIENT + HOLDING</td><td className="p-3">📝 <strong className="text-green-400">SEMNEAZĂ CONTRACT</strong></td></tr>
                    <tr className="border-b border-white/5"><td className="p-3">5</td><td className="p-3">SISTEM</td><td className="p-3">🟢 Proiect LIVE pe Browse (7 zile)</td></tr>
                    <tr className="border-b border-white/5"><td className="p-3">6</td><td className="p-3">TL + ECHIPĂ</td><td className="p-3">Aplică la proiect</td></tr>
                    <tr className="border-b border-white/5"><td className="p-3">7</td><td className="p-3">CLIENT</td><td className="p-3">Vede aplicațiile și ALEGE echipa</td></tr>
                    <tr className="border-b border-white/5 bg-green-500/5"><td className="p-3">8</td><td className="p-3">HOLDING + TL/WRK</td><td className="p-3">📝 <strong className="text-green-400">CONTRACT PROIECT</strong></td></tr>
                    <tr className="border-b border-white/5"><td className="p-3">9</td><td className="p-3">SISTEM</td><td className="p-3">📅 JOB START + FACTURĂ EMISĂ</td></tr>
                    <tr className="border-b border-white/5"><td className="p-3">10</td><td className="p-3">CLIENT</td><td className="p-3">Plătește factura</td></tr>
                    <tr><td className="p-3">11</td><td className="p-3">HOLDING</td><td className="p-3">Plătește salariile echipei</td></tr>
                  </tbody>
                </table>
              </div>
            </CardContent>
          )}
        </Card>

        {/* Section: Serviciu 2 - Stillastjenester */}
        <Card className="bg-[#0A0A0A] border-white/10">
          <CardHeader 
            className="cursor-pointer hover:bg-white/5 transition-colors"
            onClick={() => toggleSection('stillastjenester')}
          >
            <CardTitle className="flex items-center justify-between text-white">
              <div className="flex items-center gap-2">
                <Building2 className="w-5 h-5 text-[#23F7DD]" />
                🏠 Serviciu 2: STILLASTJENESTER (B2C/B2B)
                <Badge className="bg-green-500/20 text-green-400 border-green-500/30">LIVE</Badge>
              </div>
              {expandedSections.stillastjenester ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
            </CardTitle>
          </CardHeader>
          {expandedSections.stillastjenester && (
            <CardContent>
              <p className="text-[#94A3B8] mb-4 p-3 bg-[#23F7DD]/10 rounded border border-[#23F7DD]/30">
                <strong>Ce este:</strong> Client privat sau companie cere schele complete (montaj + închiriere + demontaj)
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left p-3 text-[#23F7DD]">Pas</th>
                      <th className="text-left p-3 text-[#23F7DD]">Cine</th>
                      <th className="text-left p-3 text-[#23F7DD]">Acțiune</th>
                    </tr>
                  </thead>
                  <tbody className="text-[#94A3B8]">
                    <tr className="border-b border-white/5"><td className="p-3">1</td><td className="p-3">CLIENT</td><td className="p-3">Cere ofertă</td></tr>
                    <tr className="border-b border-white/5"><td className="p-3">2</td><td className="p-3">HOLDING</td><td className="p-3">Face ofertă</td></tr>
                    <tr className="border-b border-white/5"><td className="p-3">3-4</td><td className="p-3">CLIENT + HOLDING</td><td className="p-3">Negociază prețul</td></tr>
                    <tr className="border-b border-white/5 bg-green-500/5"><td className="p-3">5</td><td className="p-3">CLIENT + HOLDING</td><td className="p-3">📝 <strong className="text-green-400">SEMNEAZĂ CONTRACT</strong></td></tr>
                    <tr className="border-b border-white/5"><td className="p-3">6</td><td className="p-3">HOLDING</td><td className="p-3">Postează pe Browse (Holding = owner)</td></tr>
                    <tr className="border-b border-white/5"><td className="p-3">7</td><td className="p-3">TL + ECHIPĂ</td><td className="p-3">Aplică la proiect</td></tr>
                    <tr className="border-b border-white/5"><td className="p-3">8</td><td className="p-3">HOLDING</td><td className="p-3">Aprobă echipa</td></tr>
                    <tr className="border-b border-white/5 bg-green-500/5"><td className="p-3">9</td><td className="p-3">HOLDING + TL/WRK</td><td className="p-3">📝 <strong className="text-green-400">CONTRACT PROIECT</strong></td></tr>
                    <tr><td className="p-3">10</td><td className="p-3">HOLDING</td><td className="p-3">📞 Ține legătura cu clientul</td></tr>
                  </tbody>
                </table>
              </div>
            </CardContent>
          )}
        </Card>

        {/* Section: Timeline */}
        <Card className="bg-[#0A0A0A] border-white/10">
          <CardHeader 
            className="cursor-pointer hover:bg-white/5 transition-colors"
            onClick={() => toggleSection('timeline')}
          >
            <CardTitle className="flex items-center justify-between text-white">
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-[#23F7DD]" />
                📅 Timeline Proiect & Aplicații
              </div>
              {expandedSections.timeline ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
            </CardTitle>
          </CardHeader>
          {expandedSections.timeline && (
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center gap-4 p-4 bg-green-500/10 rounded-lg border border-green-500/30">
                  <div className="text-center">
                    <p className="text-green-400 font-bold">ZIUA 0-7</p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-[#94A3B8]" />
                  <div>
                    <p className="text-white">🟢 Echipele pot aplica la prețul normal</p>
                    <p className="text-sm text-[#94A3B8]">⭐ Aplicațiile au PRIORITATE</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 p-4 bg-yellow-500/10 rounded-lg border border-yellow-500/30">
                  <div className="text-center">
                    <p className="text-yellow-400 font-bold">ZIUA 7</p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-[#94A3B8]" />
                  <div>
                    <p className="text-white">⏰ DEADLINE APLICAȚII</p>
                    <p className="text-sm text-[#94A3B8]">✅ Cu aplicații → se alege echipa | ⚠️ Fără aplicații → Contraoferte ON</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 p-4 bg-blue-500/10 rounded-lg border border-blue-500/30">
                  <div className="text-center">
                    <p className="text-blue-400 font-bold">ZIUA 7-10</p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-[#94A3B8]" />
                  <div>
                    <p className="text-white">💡 TL pot face contraoferte</p>
                    <p className="text-sm text-[#94A3B8]">Holding/Client decid dacă acceptă</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 p-4 bg-red-500/10 rounded-lg border border-red-500/30">
                  <div className="text-center">
                    <p className="text-red-400 font-bold">ZIUA 14</p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-[#94A3B8]" />
                  <div>
                    <p className="text-white">❌ Proiect EXPIRĂ sau se prelungește</p>
                  </div>
                </div>
              </div>
            </CardContent>
          )}
        </Card>

        {/* Section: Contraoferte */}
        <Card className="bg-[#0A0A0A] border-white/10">
          <CardHeader 
            className="cursor-pointer hover:bg-white/5 transition-colors"
            onClick={() => toggleSection('contraoferte')}
          >
            <CardTitle className="flex items-center justify-between text-white">
              <div className="flex items-center gap-2">
                <Calculator className="w-5 h-5 text-[#23F7DD]" />
                💡 Sistem Contraoferte
              </div>
              {expandedSections.contraoferte ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
            </CardTitle>
          </CardHeader>
          {expandedSections.contraoferte && (
            <CardContent className="space-y-4">
              <div className="p-4 bg-yellow-500/10 rounded-lg border border-yellow-500/30">
                <p className="text-yellow-400 font-bold mb-2">⚠️ CÂND poate TL să facă contraofertă?</p>
                <ul className="text-sm text-[#94A3B8] space-y-1">
                  <li>• După 7 zile de la publicare</li>
                  <li>• DOAR dacă proiectul NU are aplicanți</li>
                  <li>• Înainte de ziua 14 (expirare)</li>
                </ul>
              </div>

              <div className="p-4 bg-[#1e293b] rounded-lg border border-[#334155]">
                <h4 className="text-white font-bold mb-3">📊 Calculator Exemplu: TL cere +100.000 NOK extra</h4>
                <p className="text-sm text-[#94A3B8] mb-3">Proiect 200.000 NOK, 4 persoane</p>
                <table className="w-full text-sm">
                  <tbody className="text-[#94A3B8]">
                    <tr className="border-b border-white/10"><td className="p-2">Extra cerut</td><td className="p-2 text-right">100.000 NOK</td></tr>
                    <tr className="border-b border-white/10"><td className="p-2">- Platform fee (2%)</td><td className="p-2 text-right text-red-400">-2.000 NOK</td></tr>
                    <tr className="border-b border-white/10"><td className="p-2">- Andre kostnader (3%)</td><td className="p-2 text-right text-red-400">-3.000 NOK</td></tr>
                    <tr className="border-b border-white/10"><td className="p-2">- Feriepenger (10.8%)</td><td className="p-2 text-right text-red-400">-10.800 NOK</td></tr>
                    <tr className="border-b border-white/10"><td className="p-2">- AGA (14.5%)</td><td className="p-2 text-right text-red-400">-14.500 NOK</td></tr>
                    <tr className="bg-[#23F7DD]/10"><td className="p-2 font-bold text-white">Total rețineri</td><td className="p-2 text-right font-bold text-red-400">-30.300 NOK</td></tr>
                    <tr className="bg-green-500/10"><td className="p-2 font-bold text-white">Rămâne brut echipă</td><td className="p-2 text-right font-bold text-green-400">69.700 NOK</td></tr>
                    <tr className="bg-green-500/20"><td className="p-2 font-bold text-white">Per persoană extra (4)</td><td className="p-2 text-right font-bold text-[#23F7DD]">+17.425 NOK</td></tr>
                  </tbody>
                </table>
                <p className="mt-3 text-center text-[#23F7DD] font-bold">💰 NOU TOTAL BRUT: 67.425 NOK/persoană (era 50.000)</p>
              </div>
            </CardContent>
          )}
        </Card>

        {/* Section: Penalizari */}
        <Card className="bg-[#0A0A0A] border-white/10">
          <CardHeader 
            className="cursor-pointer hover:bg-white/5 transition-colors"
            onClick={() => toggleSection('penalizari')}
          >
            <CardTitle className="flex items-center justify-between text-white">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-[#23F7DD]" />
                ⚖️ Sistem Penalizări
              </div>
              {expandedSections.penalizari ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
            </CardTitle>
          </CardHeader>
          {expandedSections.penalizari && (
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="p-4 bg-[#1e293b] rounded-lg border border-[#334155]">
                  <h4 className="text-white font-bold mb-3">📊 Puncte Penalizare</h4>
                  <table className="w-full text-sm">
                    <tbody className="text-[#94A3B8]">
                      <tr className="border-b border-white/10"><td className="p-2">Retragere ÎNAINTE de semnare</td><td className="p-2 text-right">1 punct</td></tr>
                      <tr className="border-b border-white/10"><td className="p-2">Retragere DUPĂ semnare</td><td className="p-2 text-right">3 puncte</td></tr>
                      <tr className="border-b border-white/10"><td className="p-2">Retragere DUPĂ start</td><td className="p-2 text-right">5 puncte</td></tr>
                      <tr><td className="p-2">Abandon în timpul proiectului</td><td className="p-2 text-right">10 puncte</td></tr>
                    </tbody>
                  </table>
                </div>
                <div className="p-4 bg-[#1e293b] rounded-lg border border-[#334155]">
                  <h4 className="text-white font-bold mb-3">🚦 Consecințe</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between items-center p-2 bg-green-500/10 rounded">
                      <span className="text-[#94A3B8]">0-2 puncte</span>
                      <Badge className="bg-green-500/20 text-green-400">✅ Normal</Badge>
                    </div>
                    <div className="flex justify-between items-center p-2 bg-yellow-500/10 rounded">
                      <span className="text-[#94A3B8]">3-5 puncte</span>
                      <Badge className="bg-yellow-500/20 text-yellow-400">⚠️ Avertisment</Badge>
                    </div>
                    <div className="flex justify-between items-center p-2 bg-orange-500/10 rounded">
                      <span className="text-[#94A3B8]">6-9 puncte</span>
                      <Badge className="bg-orange-500/20 text-orange-400">🟠 30 zile ban</Badge>
                    </div>
                    <div className="flex justify-between items-center p-2 bg-red-500/10 rounded">
                      <span className="text-[#94A3B8]">10+ puncte</span>
                      <Badge className="bg-red-500/20 text-red-400">🔴 90 zile + review</Badge>
                    </div>
                  </div>
                </div>
              </div>
              <div className="p-3 bg-green-500/10 rounded border border-green-500/30">
                <p className="text-green-400 font-semibold">🔄 Resetare: -1 punct/proiect reușit | Reset complet la 12 luni fără incidente</p>
              </div>
            </CardContent>
          )}
        </Card>

        {/* Section: Facturare */}
        <Card className="bg-[#0A0A0A] border-white/10">
          <CardHeader 
            className="cursor-pointer hover:bg-white/5 transition-colors"
            onClick={() => toggleSection('facturare')}
          >
            <CardTitle className="flex items-center justify-between text-white">
              <div className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#23F7DD]" />
                💵 Facturare Automată
              </div>
              {expandedSections.facturare ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
            </CardTitle>
          </CardHeader>
          {expandedSections.facturare && (
            <CardContent>
              <div className="p-4 bg-[#23F7DD]/10 rounded-lg border border-[#23F7DD]/30 mb-4">
                <p className="text-white">📅 <strong>CÂND:</strong> Factura se emite în <span className="text-[#23F7DD] font-bold">PRIMA ZI</span> când începe lucrul (JOB START)</p>
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left p-3 text-[#23F7DD]">Valoare Proiect</th>
                    <th className="text-left p-3 text-[#23F7DD]">Scadență</th>
                  </tr>
                </thead>
                <tbody className="text-[#94A3B8]">
                  <tr className="border-b border-white/5"><td className="p-3">Sub 125.000 NOK</td><td className="p-3 font-bold text-white">7 zile</td></tr>
                  <tr className="border-b border-white/5"><td className="p-3">125.000 - 250.000 NOK</td><td className="p-3 font-bold text-white">14 zile</td></tr>
                  <tr><td className="p-3">Peste 250.000 NOK</td><td className="p-3 font-bold text-white">30 zile</td></tr>
                </tbody>
              </table>
            </CardContent>
          )}
        </Card>

        {/* Section: Structura Retineri */}
        <Card className="bg-[#0A0A0A] border-white/10">
          <CardHeader 
            className="cursor-pointer hover:bg-white/5 transition-colors"
            onClick={() => toggleSection('retineri')}
          >
            <CardTitle className="flex items-center justify-between text-white">
              <div className="flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-[#23F7DD]" />
                💰 Structura Rețineri
              </div>
              {expandedSections.retineri ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
            </CardTitle>
          </CardHeader>
          {expandedSections.retineri && (
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="p-4 bg-[#1e293b] rounded-lg border border-[#334155]">
                  <h4 className="text-white font-bold mb-3">🏢 HOLDING plătește (peste brut)</h4>
                  <table className="w-full text-sm">
                    <tbody className="text-[#94A3B8]">
                      <tr className="border-b border-white/10"><td className="p-2">AGA</td><td className="p-2 text-right font-bold text-white">14.5%</td><td className="p-2 text-right text-xs">→ la stat</td></tr>
                      <tr><td className="p-2">Feriepenger</td><td className="p-2 text-right font-bold text-white">10.8%</td><td className="p-2 text-right text-xs">→ în iunie</td></tr>
                    </tbody>
                  </table>
                </div>
                <div className="p-4 bg-[#1e293b] rounded-lg border border-[#334155]">
                  <h4 className="text-white font-bold mb-3">👷 Se REȚINE din salariu</h4>
                  <table className="w-full text-sm">
                    <tbody className="text-[#94A3B8]">
                      <tr className="border-b border-white/10"><td className="p-2">Platform fee</td><td className="p-2 text-right font-bold text-[#23F7DD]">2.0%</td><td className="p-2 text-right text-xs">→ AllStillas</td></tr>
                      <tr className="border-b border-white/10"><td className="p-2">Andre kostnader</td><td className="p-2 text-right font-bold text-white">3.0%</td><td className="p-2 text-right text-xs">→ pensjon</td></tr>
                      <tr><td className="p-2">Skatt</td><td className="p-2 text-right font-bold text-white">Personal</td><td className="p-2 text-right text-xs">→ variază</td></tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="p-4 bg-[#1e293b] rounded-lg border border-[#334155]">
                <h4 className="text-white font-bold mb-3">📊 Exemplu: TL primește 100.000 NOK brut</h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-[#23F7DD] font-semibold mb-2">HOLDING PLĂTEȘTE:</p>
                    <table className="w-full text-sm">
                      <tbody className="text-[#94A3B8]">
                        <tr><td className="p-1">Brut TL</td><td className="p-1 text-right">100.000 NOK</td></tr>
                        <tr><td className="p-1">+ AGA (14.5%)</td><td className="p-1 text-right">+14.500 NOK</td></tr>
                        <tr><td className="p-1">+ Feriepenger (10.8%)</td><td className="p-1 text-right">+10.800 NOK</td></tr>
                        <tr className="bg-red-500/10"><td className="p-1 font-bold">COST TOTAL</td><td className="p-1 text-right font-bold text-red-400">125.300 NOK</td></tr>
                      </tbody>
                    </table>
                  </div>
                  <div>
                    <p className="text-[#23F7DD] font-semibold mb-2">TL PRIMEȘTE:</p>
                    <table className="w-full text-sm">
                      <tbody className="text-[#94A3B8]">
                        <tr><td className="p-1">Brut</td><td className="p-1 text-right">100.000 NOK</td></tr>
                        <tr><td className="p-1">- Platform fee (2%)</td><td className="p-1 text-right">-2.000 NOK</td></tr>
                        <tr><td className="p-1">- Andre kostnader (3%)</td><td className="p-1 text-right">-3.000 NOK</td></tr>
                        <tr><td className="p-1">- Skatt (~30%)</td><td className="p-1 text-right">-28.500 NOK</td></tr>
                        <tr className="bg-green-500/10"><td className="p-1 font-bold">NET LA PLATĂ</td><td className="p-1 text-right font-bold text-green-400">66.500 NOK</td></tr>
                        <tr><td className="p-1">+ Feriepenger iunie</td><td className="p-1 text-right text-[#23F7DD]">+10.800 NOK</td></tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </CardContent>
          )}
        </Card>

        {/* Section: Test Flows Checklist */}
        <Card className="bg-[#0A0A0A] border-[#23F7DD]/30">
          <CardHeader 
            className="cursor-pointer hover:bg-white/5 transition-colors"
            onClick={() => toggleSection('testFlows')}
          >
            <CardTitle className="flex items-center justify-between text-white">
              <div className="flex items-center gap-2">
                <ClipboardCheck className="w-5 h-5 text-[#23F7DD]" />
                🧪 Test Flows Checklist
                <Badge className="bg-[#23F7DD]/20 text-[#23F7DD] border-[#23F7DD]/30">
                  {Object.values(testChecklist).filter(Boolean).length}/{Object.keys(testChecklist).length} Complete
                </Badge>
              </div>
              {expandedSections.testFlows ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
            </CardTitle>
          </CardHeader>
          {expandedSections.testFlows && (
            <CardContent className="space-y-6">
              {/* Reset Button */}
              <div className="flex justify-end">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={resetChecklist}
                  className="text-red-400 border-red-400/30 hover:bg-red-400/10"
                >
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Reset All
                </Button>
              </div>
              
              {/* Company Client Flow */}
              <div className="p-4 bg-[#1e293b] rounded-lg border border-blue-500/30">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-blue-400 font-bold flex items-center gap-2">
                    <Building2 className="w-5 h-5" />
                    🏢 COMPANY CLIENT Flow (Bedrift)
                  </h4>
                  <Badge className={`${getProgress(['cc_create_project','cc_holding_review','cc_holding_approve','cc_client_sign','cc_tl_apply','cc_client_select_team','cc_team_sign','cc_project_live']).percent === 100 ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                    {getProgress(['cc_create_project','cc_holding_review','cc_holding_approve','cc_client_sign','cc_tl_apply','cc_client_select_team','cc_team_sign','cc_project_live']).checked}/8
                  </Badge>
                </div>
                <p className="text-sm text-[#94A3B8] mb-4">Client crează proiect → Selectează echipa singur</p>
                <div className="space-y-3">
                  <FlowStep checked={testChecklist.cc_create_project} onToggle={() => toggleCheck('cc_create_project')} step={1} label="Client creează proiect (workforce/stillastjenester)" />
                  <FlowStep checked={testChecklist.cc_holding_review} onToggle={() => toggleCheck('cc_holding_review')} step={2} label="Holding Admin vede proiectul în review" />
                  <FlowStep checked={testChecklist.cc_holding_approve} onToggle={() => toggleCheck('cc_holding_approve')} step={3} label="Holding Admin aprobă proiectul" />
                  <FlowStep checked={testChecklist.cc_client_sign} onToggle={() => toggleCheck('cc_client_sign')} step={4} label="Client semnează contractul" />
                  <FlowStep checked={testChecklist.cc_tl_apply} onToggle={() => toggleCheck('cc_tl_apply')} step={5} label="Team Leader aplică cu echipa" />
                  <FlowStep checked={testChecklist.cc_client_select_team} onToggle={() => toggleCheck('cc_client_select_team')} step={6} label="CLIENT selectează echipa" role="client" />
                  <FlowStep checked={testChecklist.cc_team_sign} onToggle={() => toggleCheck('cc_team_sign')} step={7} label="Team semnează contractul" />
                  <FlowStep checked={testChecklist.cc_project_live} onToggle={() => toggleCheck('cc_project_live')} step={8} label="Proiect LIVE - lucru în curs" />
                </div>
              </div>
              
              {/* Private Client Flow */}
              <div className="p-4 bg-[#1e293b] rounded-lg border border-purple-500/30">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-purple-400 font-bold flex items-center gap-2">
                    <Users className="w-5 h-5" />
                    👤 PRIVATE CLIENT Flow (Privat)
                  </h4>
                  <Badge className={`${getProgress(['pc_request_quote','pc_holding_send_quote','pc_client_accept','pc_client_sign','pc_tl_apply','pc_holding_select_team','pc_team_sign','pc_project_live']).percent === 100 ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                    {getProgress(['pc_request_quote','pc_holding_send_quote','pc_client_accept','pc_client_sign','pc_tl_apply','pc_holding_select_team','pc_team_sign','pc_project_live']).checked}/8
                  </Badge>
                </div>
                <p className="text-sm text-[#94A3B8] mb-4">Client cere ofertă → Holding selectează echipa</p>
                <div className="space-y-3">
                  <FlowStep checked={testChecklist.pc_request_quote} onToggle={() => toggleCheck('pc_request_quote')} step={1} label="Client privat cere ofertă (Request Quote)" />
                  <FlowStep checked={testChecklist.pc_holding_send_quote} onToggle={() => toggleCheck('pc_holding_send_quote')} step={2} label="Holding trimite oferta cu preț" />
                  <FlowStep checked={testChecklist.pc_client_accept} onToggle={() => toggleCheck('pc_client_accept')} step={3} label="Client acceptă oferta" />
                  <FlowStep checked={testChecklist.pc_client_sign} onToggle={() => toggleCheck('pc_client_sign')} step={4} label="Client semnează contractul" />
                  <FlowStep checked={testChecklist.pc_tl_apply} onToggle={() => toggleCheck('pc_tl_apply')} step={5} label="Team Leader aplică cu echipa" />
                  <FlowStep checked={testChecklist.pc_holding_select_team} onToggle={() => toggleCheck('pc_holding_select_team')} step={6} label="HOLDING selectează echipa" role="holding" />
                  <FlowStep checked={testChecklist.pc_team_sign} onToggle={() => toggleCheck('pc_team_sign')} step={7} label="Team semnează contractul" />
                  <FlowStep checked={testChecklist.pc_project_live} onToggle={() => toggleCheck('pc_project_live')} step={8} label="Proiect LIVE - lucru în curs" />
                </div>
              </div>
              
              {/* Team Leader Flow */}
              <div className="p-4 bg-[#1e293b] rounded-lg border border-orange-500/30">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-orange-400 font-bold flex items-center gap-2">
                    <Users className="w-5 h-5" />
                    👷 TEAM LEADER Flow
                  </h4>
                  <Badge className={`${getProgress(['tl_browse_projects','tl_apply_with_team','tl_wait_selection','tl_sign_contract','tl_daily_progress','tl_complete_project']).percent === 100 ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                    {getProgress(['tl_browse_projects','tl_apply_with_team','tl_wait_selection','tl_sign_contract','tl_daily_progress','tl_complete_project']).checked}/6
                  </Badge>
                </div>
                <div className="space-y-3">
                  <FlowStep checked={testChecklist.tl_browse_projects} onToggle={() => toggleCheck('tl_browse_projects')} step={1} label="Browse Projects - vede proiecte disponibile" />
                  <FlowStep checked={testChecklist.tl_apply_with_team} onToggle={() => toggleCheck('tl_apply_with_team')} step={2} label="Aplică la proiect cu echipa (selectează workers)" />
                  <FlowStep checked={testChecklist.tl_wait_selection} onToggle={() => toggleCheck('tl_wait_selection')} step={3} label="Dashboard - vede status aplicație (pending/selected)" />
                  <FlowStep checked={testChecklist.tl_sign_contract} onToggle={() => toggleCheck('tl_sign_contract')} step={4} label="Semnează contractul de proiect" />
                  <FlowStep checked={testChecklist.tl_daily_progress} onToggle={() => toggleCheck('tl_daily_progress')} step={5} label="Adaugă Daily Progress updates" />
                  <FlowStep checked={testChecklist.tl_complete_project} onToggle={() => toggleCheck('tl_complete_project')} step={6} label="Marchează proiectul ca COMPLET" />
                </div>
              </div>
              
              {/* Worker Flow */}
              <div className="p-4 bg-[#1e293b] rounded-lg border border-green-500/30">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-green-400 font-bold flex items-center gap-2">
                    <Users className="w-5 h-5" />
                    🔧 WORKER Flow
                  </h4>
                  <Badge className={`${getProgress(['w_see_dashboard','w_view_applications','w_assigned_to_project','w_work_on_project']).percent === 100 ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                    {getProgress(['w_see_dashboard','w_view_applications','w_assigned_to_project','w_work_on_project']).checked}/4
                  </Badge>
                </div>
                <div className="space-y-3">
                  <FlowStep checked={testChecklist.w_see_dashboard} onToggle={() => toggleCheck('w_see_dashboard')} step={1} label="Vede Dashboard cu statistici" />
                  <FlowStep checked={testChecklist.w_view_applications} onToggle={() => toggleCheck('w_view_applications')} step={2} label="Vede My Applications (unde e inclus în echipă)" />
                  <FlowStep checked={testChecklist.w_assigned_to_project} onToggle={() => toggleCheck('w_assigned_to_project')} step={3} label="Primește notificare când e asignat" />
                  <FlowStep checked={testChecklist.w_work_on_project} onToggle={() => toggleCheck('w_work_on_project')} step={4} label="Lucrează pe proiect" />
                </div>
              </div>
              
              {/* Holding Admin Flow */}
              <div className="p-4 bg-[#1e293b] rounded-lg border border-yellow-500/30">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-yellow-400 font-bold flex items-center gap-2">
                    <Building2 className="w-5 h-5" />
                    🏢 HOLDING ADMIN Flow
                  </h4>
                  <Badge className={`${getProgress(['ha_review_projects','ha_approve_reject','ha_send_counteroffer','ha_select_team_private','ha_manage_workers','ha_financial_dashboard']).percent === 100 ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                    {getProgress(['ha_review_projects','ha_approve_reject','ha_send_counteroffer','ha_select_team_private','ha_manage_workers','ha_financial_dashboard']).checked}/6
                  </Badge>
                </div>
                <div className="space-y-3">
                  <FlowStep checked={testChecklist.ha_review_projects} onToggle={() => toggleCheck('ha_review_projects')} step={1} label="Vede proiecte în Pending Review" />
                  <FlowStep checked={testChecklist.ha_approve_reject} onToggle={() => toggleCheck('ha_approve_reject')} step={2} label="Aprobă sau Respinge proiecte" />
                  <FlowStep checked={testChecklist.ha_send_counteroffer} onToggle={() => toggleCheck('ha_send_counteroffer')} step={3} label="Trimite Counter-offer la client" />
                  <FlowStep checked={testChecklist.ha_select_team_private} onToggle={() => toggleCheck('ha_select_team_private')} step={4} label="Selectează echipa pentru Private Clients" />
                  <FlowStep checked={testChecklist.ha_manage_workers} onToggle={() => toggleCheck('ha_manage_workers')} step={5} label="Gestionează Workers (approve, ratings)" />
                  <FlowStep checked={testChecklist.ha_financial_dashboard} onToggle={() => toggleCheck('ha_financial_dashboard')} step={6} label="Vede Financial Dashboard" />
                </div>
              </div>
              
              {/* Platform Admin Flow */}
              <div className="p-4 bg-[#1e293b] rounded-lg border border-[#23F7DD]/30">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-[#23F7DD] font-bold flex items-center gap-2">
                    <Building2 className="w-5 h-5" />
                    🔷 PLATFORM ADMIN Flow
                  </h4>
                  <Badge className={`${getProgress(['pa_manage_webshop','pa_manage_rentals','pa_manage_users','pa_view_analytics']).percent === 100 ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                    {getProgress(['pa_manage_webshop','pa_manage_rentals','pa_manage_users','pa_view_analytics']).checked}/4
                  </Badge>
                </div>
                <div className="space-y-3">
                  <FlowStep checked={testChecklist.pa_manage_webshop} onToggle={() => toggleCheck('pa_manage_webshop')} step={1} label="Gestionează Webshop (vendori, comisioane)" />
                  <FlowStep checked={testChecklist.pa_manage_rentals} onToggle={() => toggleCheck('pa_manage_rentals')} step={2} label="Gestionează Rentals" />
                  <FlowStep checked={testChecklist.pa_manage_users} onToggle={() => toggleCheck('pa_manage_users')} step={3} label="Gestionează Users & Roles" />
                  <FlowStep checked={testChecklist.pa_view_analytics} onToggle={() => toggleCheck('pa_view_analytics')} step={4} label="Vede Analytics & Revenue" />
                </div>
              </div>
            </CardContent>
          )}
        </Card>

        {/* Footer */}
        <div className="text-center pt-8 border-t border-white/10">
          <p className="text-[#64748B]">📄 AllStillas Platform Roadmap v1.0</p>
          <p className="text-[#64748B] text-sm">Ultima actualizare: Ianuarie 2025</p>
        </div>
      </div>
    </DashboardLayout>
  );
}

// Flow Step Component with Checkbox
function FlowStep({ checked, onToggle, step, label, role }) {
  return (
    <div 
      className={`flex items-center gap-3 p-2 rounded cursor-pointer transition-all ${checked ? 'bg-green-500/10' : 'hover:bg-white/5'}`}
      onClick={onToggle}
    >
      <Checkbox checked={checked} className="data-[state=checked]:bg-green-500 data-[state=checked]:border-green-500" />
      <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${checked ? 'bg-green-500 text-black' : 'bg-white/10 text-white'}`}>
        {step}
      </span>
      <span className={`flex-1 ${checked ? 'text-green-400 line-through' : 'text-white'}`}>{label}</span>
      {role && (
        <Badge className={`${role === 'client' ? 'bg-blue-500/20 text-blue-400' : 'bg-yellow-500/20 text-yellow-400'} border-0 text-xs`}>
          {role === 'client' ? 'CLIENT' : 'HOLDING'}
        </Badge>
      )}
    </div>
  );
}
